import { Permission } from '@frontend/authorization';
import { ClassType, EntityType, useAppSelector } from '@frontend/common';
import { HorizontalButtonGroupButton } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { ListInfo, changeFilter, listStore } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import { useSelector } from 'react-redux';

import ObjectListButtonGroup from '../object-list-button-group/object-list-button-group.component';
import { MultiSelectOptions } from './object-list.component';

interface Props<T> {
    type: EntityType;
    create?: {
        label?: React.ReactNode;
        onCreate: () => void;
        icon?: HorizontalButtonGroupButton['icon'];
        requiredPermissions?: Permission[];
        overwriteCreateLabel?: React.ReactNode;
    };
    multiSelectOptions?: MultiSelectOptions<T>;
    customButtons?: HorizontalButtonGroupButton[];
    selectedObjects: T[];
    includeDeleted?: {
        requiredPermissions: Permission[];
    };
    dispatch?: ThunkDispatch<any, any, Action>;
    listId: string;
    onMultiDelete: () => void;
}

export const ObjectListActionButtons = <T,>(props: Props<T>) => {
    const store = useAppSelector(useSelector, listStore);
    const list: ListInfo | undefined = store[props.listId];
    return (
        <ObjectListButtonGroup
            buttons={[
                ...(props.create
                    ? [
                          {
                              hide: false,
                              onClick: props.create.onCreate,
                              icon: props.create.icon ?? ICONS.BUTTON.CREATE,
                              id: 'ObjectList.CreateButton',
                              text: props.create.overwriteCreateLabel
                                  ? props.create.overwriteCreateLabel
                                  : CommonMessage.FORMS.CREATE_OBJECT(props.create.label ? props.create.label : props.type.replace(/_/g, ' ')),
                              requiredPermissions: props.create.requiredPermissions,
                              className:
                                  'bg-brand-primary text-always-white disabled:bg-brand-disabled disabled:text-brand-muted hover:bg-brand-secondary inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-md px-4 text-base disabled:cursor-not-allowed'
                          }
                      ]
                    : []),
                ...(props.multiSelectOptions?.delete
                    ? [
                          {
                              hide: !props.multiSelectOptions?.delete?.enabled(props.selectedObjects),
                              onClick: () => props.onMultiDelete(),
                              icon: props.multiSelectOptions.delete.icon ?? ICONS.BUTTON.DELETE,
                              id: 'ObjectList.DeleteButton',
                              text: props.multiSelectOptions.delete.label ?? CommonMessage.BUTTONS.DELETE,
                              requiredPermissions: props.multiSelectOptions.delete.requiredPermissions,
                              className: 'btn bg-status-danger text-always-white'
                          }
                      ]
                    : []),
                ...(props.multiSelectOptions?.custom
                    ? props.multiSelectOptions.custom.map((button) => ({
                          hide: button.enabled ? !button.enabled(props.selectedObjects) : false,
                          disabled: button.enabled ? !button.enabled(props.selectedObjects) : button.disabled,
                          requiredPermissions: button.requiredPermissions,
                          ...button
                      }))
                    : []),
                ...(props.customButtons ? props.customButtons : []),
                ...(props.includeDeleted
                    ? [
                          {
                              hide: false,
                              onClick: () =>
                                  props.dispatch &&
                                  props.dispatch(
                                      changeFilter({
                                          listId: props.listId,
                                          key: 'include_deleted',
                                          value: list.filter?.['include_deleted'] === 'true' ? null : 'true'
                                      })
                                  ),
                              type: list?.filter?.['include_deleted'] === 'true' ? ClassType.SUCCESS : ClassType.LIGHT,
                              id: 'ObjectList.IncludeDeletedButton',
                              text: CommonMessage.BUTTONS.INCLUDE_DELETED,
                              requiredPermissions: props.includeDeleted.requiredPermissions
                          }
                      ]
                    : [])
            ]}
        />
    );
};
